import { useState, useEffect } from 'react';
import { useAuth } from '../context/authContext';
import { supabase } from '../supabaseClient';
import { useNavigate } from 'react-router-dom';

export default function CreateModule() {
  const { user } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [moduleLessons, setModuleLessons] = useState([]);
  const [selectedLessonIds, setSelectedLessonIds] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchModuleLessons = async () => {
      const { data, error } = await supabase
        .from('module_lessons')
        .select('id, title');

      if (error) {
        console.error('Error loading module lessons:', error.message);
        setError('Failed to load module lessons.');
      } else {
        setModuleLessons(data);
      }
    };

    fetchModuleLessons();
  }, []);

  const handleCreateModule = async () => {
    setError('');
    setSuccess('');

    if (!name.trim()) {
      setError('Module name is required.');
      return;
    }

    // 1. Insert module
    const { data: module, error: insertError } = await supabase
      .from('modules')
      .insert([
        {
          name,
          description,
          created_by: user.id,
          is_public: false,
        },
      ])
      .select()
      .single();

    if (insertError) {
      console.error('Error creating module:', insertError.message);
      setError('Failed to create module.');
      return;
    }

    const moduleId = module.id;

    // 2. Link lessons to the module
    // eslint-disable-next-line no-unused-vars
    const lessonLinks = selectedLessonIds.map((lessonId) => ({
      module_id: moduleId,
      lesson_id: lessonId,
    }));
    // eslint-disable-next-line no-unused-vars
    const { error: linkError } = await supabase
      .from('module_progress') // linking is done by creating progress rows (or use a linking table if you want)
      .upsert([]); // we assume lessons are auto-detected from module_lessons

    // Note: If you want to track linkage more explicitly, create a separate linking table.

    setSuccess('Module created successfully!');
    setName('');
    setDescription('');
    setSelectedLessonIds([]);
    navigate('/admin/manage-modules');
  };

  const toggleLesson = (lessonId) => {
    setSelectedLessonIds((prev) =>
      prev.includes(lessonId)
        ? prev.filter((id) => id !== lessonId)
        : [...prev, lessonId]
    );
  };

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white rounded shadow space-y-4">
      <h1 className="text-2xl font-bold">Create Module</h1>

      <input
        type="text"
        placeholder="Module Name"
        className="w-full border rounded p-2"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />

      <textarea
        placeholder="Module Description (optional)"
        className="w-full border rounded p-2"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />

      <div>
        <p className="font-semibold mb-2">Select Lessons (from module lessons only):</p>
        <div className="max-h-48 overflow-y-auto border rounded p-2">
          {moduleLessons.length === 0 ? (
            <p className="text-gray-500">No module lessons available.</p>
          ) : (
            moduleLessons.map((lesson) => (
              <label key={lesson.id} className="block text-sm">
                <input
                  type="checkbox"
                  checked={selectedLessonIds.includes(lesson.id)}
                  onChange={() => toggleLesson(lesson.id)}
                  className="mr-2"
                />
                {lesson.title}
              </label>
            ))
          )}
        </div>
      </div>

      <button
        onClick={handleCreateModule}
        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-500"
      >
        Create Module
      </button>

      {error && <p className="text-red-600">{error}</p>}
      {success && <p className="text-green-600">{success}</p>}
    </div>
  );
}
